import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { z } from "zod";
// Local
import { guardIsPlainObject, zFallback } from "../utils";
import { NotFoundError } from "../error-classes";

export type StreamProgressType = {
  /** i.e.: "connecting", "fetching", "geocoding", "converting", "firestore-write", "typesense-write", "completed" */
  step: string;
  message: string;
  /** 0-100. -1 on error. overall progress. */
  progress: number;
  /** 0-100. progress within the current step. */
  stepProgress?: number;
  details?: Record<string, unknown>;
};

export const EnumQBOImportJobType = {
  CUSTOMERS: "customers",
  PRICE_BOOK_ITEMS: "priceBookItems",
  INVOICES_AND_PAYMENTS: "invoicesAndPayments",
} as const;
export type QBOImportJobType =
  (typeof EnumQBOImportJobType)[keyof typeof EnumQBOImportJobType];

export const EnumQBOImportJobStatus = {
  PENDING: "pending",
  IN_PROGRESS: "in-progress",
  COMPLETED: "completed",
  FAILED: "failed",
} as const;
export type QBOImportJobStatus =
  (typeof EnumQBOImportJobStatus)[keyof typeof EnumQBOImportJobStatus];

function readableStatus(status: QBOImportJobStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "in-progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    default:
      const _exhaustiveCheck: never = status;
      return _exhaustiveCheck;
  }
}

/** lives @ `siteKeys/{siteKey}/qboImportJobs/{qboImportJobID}` */
export interface QBOImportJob {
  id: string;
  refPath: string;
  type: QBOImportJobType;
  uid: string;
  status: QBOImportJobStatus;
  data: StreamProgressType;
  timestampCreated: Timestamp;
  timestampLastModified: Timestamp;
}

// with fallbacks is the only available schema, cus we're only gonna read this doc
const schema = z.object({
  type: z.enum(["customers", "priceBookItems", "invoicesAndPayments"]),
  uid: zFallback(z.string(), "unknown", "QBOImportJob.uid"),
  //   status: zFallback(
  //     z.enum(["pending", "in-progress", "completed", "failed"]),
  //     "pending", // maybe change this to "failed"
  //     "QBOImportJob.status",
  //   ),
  status: z.enum(["pending", "in-progress", "completed", "failed"]),
  data: z.object({
    step: zFallback(z.string(), "unknown", "QBOImportJob.data.step"),
    message: zFallback(z.string(), "unknown", "QBOImportJob.data.message"),
    // progress: zFallback(z.number(), 0, "QBOImportJob.data.progress"),
    progress: z.number(),

    stepProgress: zFallback(
      z.number().optional(),
      undefined,
      "QBOImportJob.data.stepProgress",
    ),
    details: zFallback(
      z.record(z.any()).optional(),
      undefined,
      "QBOImportJob.data.details",
    ),
  }),
  timestampCreated: zFallback(
    z.instanceof(Timestamp),
    Timestamp.now(),
    "QBOImportJob.timestampCreated",
  ),
  timestampLastModified: zFallback(
    z.instanceof(Timestamp),
    Timestamp.now(),
    "QBOImportJob.timestampLastModified",
  ),
});

export const QBOImportJobManager = {
  createFromSnapshot: createFromSnapshot,
  readableStatus: readableStatus,
};

function createFromSnapshot(snapshot: DocumentSnapshot): QBOImportJob {
  if (!snapshot.exists) {
    throw new NotFoundError(
      `Document does not exist. refPath: ${snapshot.ref.path}`,
    );
  }
  const data = snapshot.data();
  if (!guardIsPlainObject(data)) {
    throw new Error(`snapshot.data() not an object: ${JSON.stringify(data)}`);
  }
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...schema.parse(data),
  };
}
